import {Offer} from '@findhotel/sapi'
import {Hotel} from 'modules/sapiSearch/slice'

import {
  BREAKFAST_INCLUDED_MEALS,
  getMealsAmenities
} from '@vio/core/src/offer/business/meal'

import {
  comparisonTolerance,
  MAX_PERKS,
  MIN_CHEAPER_SAVING,
  SIMILAR_PRICE_RANGE,
  SIMILAR_RATING_RANGE
} from '../constants'

import type {SimilarHotelPerk} from '../types'

const positive = (value: number | undefined) =>
  Number.isFinite(value) && value > 0

const combinePerkAvailability = (
  values: (boolean | undefined)[]
): boolean | undefined => {
  if (values.every(value => value === true)) return true
  if (values.includes(false)) return false

  return undefined
}

const hasFreeCancellation = (offer: Offer) => {
  if (offer.cancellationPenalties?.length === 0) return false

  const amount = offer.cancellationPenalties?.[0]?.amount

  if (amount == null) return undefined

  return Number(amount) === 0
}

const hasBreakfast = (offer: Offer) => {
  if (!offer.package?.amenities) return undefined

  return getMealsAmenities(offer).some(meal =>
    BREAKFAST_INCLUDED_MEALS.includes(meal)
  )
}

// A split booking only includes a perk when every part includes it.
const offerPerks = (offers?: Offer[]) => {
  if (!offers?.length) return {}

  return {
    freeCancellation: combinePerkAvailability(offers.map(hasFreeCancellation)),
    breakfast: combinePerkAvailability(offers.map(hasBreakfast))
  }
}

const getOfferComparisonPerks = (
  offers?: Offer[],
  anchorOffers?: Offer[]
): SimilarHotelPerk[] => {
  const candidate = offerPerks(offers)
  const reference = offerPerks(anchorOffers)
  const perks: SimilarHotelPerk[] = []

  if (
    candidate.freeCancellation === true &&
    reference.freeCancellation === false
  )
    perks.push({kind: 'freeCancellation'})

  if (candidate.breakfast === true && reference.breakfast === false)
    perks.push({kind: 'breakfast'})

  return perks
}

const getPricePerks = (difference?: number): SimilarHotelPerk[] => {
  if (!Number.isFinite(difference)) return []

  if (Math.abs(difference) <= SIMILAR_PRICE_RANGE + comparisonTolerance)
    return [{kind: 'similarPrice'}]

  if (difference < -MIN_CHEAPER_SAVING - comparisonTolerance)
    return [
      {
        kind: 'cheaper',
        value: Math.floor(-difference * 100 + comparisonTolerance)
      }
    ]

  return []
}

const getRatingPerks = (hotel: Hotel, anchor: Hotel): SimilarHotelPerk[] => {
  const rating = hotel.guestRating?.overall
  const anchorRating = anchor.guestRating?.overall

  if (!positive(rating) || !positive(anchorRating)) return []

  const difference = rating - anchorRating

  if (Math.abs(difference) <= SIMILAR_RATING_RANGE + comparisonTolerance)
    return [{kind: 'similarRating'}]

  if (difference > SIMILAR_RATING_RANGE) return [{kind: 'betterRating'}]

  return []
}

const getStarRatingPerks = (
  hotel: Hotel,
  anchor: Hotel
): SimilarHotelPerk[] => {
  if (!positive(hotel.starRating) || !positive(anchor.starRating)) return []

  if (hotel.starRating > anchor.starRating) return [{kind: 'stepUp'}]
  if (hotel.starRating === anchor.starRating) return [{kind: 'sameStars'}]

  return []
}

const getReviewCountPerks = (
  hotel: Hotel,
  anchor: Hotel
): SimilarHotelPerk[] => {
  if (
    positive(hotel.reviewCount) &&
    positive(anchor.reviewCount) &&
    hotel.reviewCount > anchor.reviewCount
  )
    return [{kind: 'moreReviewed'}]

  return []
}

/** Select supported comparisons in product priority order, without padding empty slots. */
export const getSimilarHotelPerks = ({
  anchor,
  hotel,
  priceDifference,
  anchorOffers,
  offers
}: {
  anchor?: Hotel
  hotel: Hotel
  priceDifference?: number
  anchorOffers?: Offer[]
  offers?: Offer[]
}): SimilarHotelPerk[] => {
  if (!anchor) return []

  const perks = [
    ...getOfferComparisonPerks(offers, anchorOffers),
    ...getPricePerks(priceDifference),
    ...getRatingPerks(hotel, anchor),
    ...getStarRatingPerks(hotel, anchor),
    ...getReviewCountPerks(hotel, anchor)
  ]

  return perks.slice(0, MAX_PERKS)
}
